import {
  RESYNC_EVENT,
  type StreamChannel,
  type StreamMessage,
  StreamMessageSchema,
} from '@vision360/contracts';
import { apiUrl } from './origin';

/**
 * The live stream, TechSpec §7 / PRD VS-043.
 *
 * Read with fetch and a small SSE parser rather than EventSource: EventSource
 * cannot send an Authorization header, and the access token never goes in a
 * URL. Reconnects with exponential backoff and jitter capped at 30 s, sends
 * `Last-Event-ID` itself, and reports `resync` when the server says the gap
 * outran its buffer — the caller then refetches.
 */

export type StreamState = 'connecting' | 'live' | 'reconnecting';

export interface StreamOptions {
  channels: readonly StreamChannel[];
  /** The current access token, refreshed by the session when needed. */
  accessToken: () => Promise<string | null>;
  onMessage: (message: StreamMessage) => void;
  onResync: () => void;
  onState?: (state: StreamState) => void;
  fetch?: typeof fetch;
  random?: () => number;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
}

export const BACKOFF_BASE_MS = 1_000;
export const BACKOFF_CAP_MS = 30_000;

/** Full jitter: uniform in [0, min(cap, base·2^attempt)]. */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  return Math.round(random() * Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt));
}

interface Frame {
  id: string | null;
  event: string;
  data: string;
}

/** Parses complete SSE frames out of `buffer`; returns them and the unparsed rest. */
export function parseFrames(buffer: string): { frames: Frame[]; rest: string } {
  const frames: Frame[] = [];
  const blocks = buffer.replace(/\r\n/g, '\n').split('\n\n');
  const rest = blocks.pop() ?? '';
  for (const block of blocks) {
    let id: string | null = null;
    let event = 'message';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (line === '' || line.startsWith(':')) continue;
      const colon = line.indexOf(':');
      const field = colon < 0 ? line : line.slice(0, colon);
      const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
      if (field === 'id') id = value;
      else if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    if (id !== null || data.length > 0 || event !== 'message') {
      frames.push({ id, event, data: data.join('\n') });
    }
  }
  return { frames, rest };
}

const defaultSleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    });
  });

/** Opens the stream; returns a function that closes it for good. */
export function openStream(options: StreamOptions): () => void {
  const doFetch = options.fetch ?? fetch;
  const sleep = options.sleep ?? defaultSleep;
  const stop = new AbortController();
  let lastEventId: string | null = null;

  async function connectOnce(): Promise<'ended' | 'unauthorized'> {
    const token = await options.accessToken();
    if (!token) return 'unauthorized';
    const headers: Record<string, string> = {
      authorization: `Bearer ${token}`,
      accept: 'text/event-stream',
    };
    if (lastEventId) headers['last-event-id'] = lastEventId;
    const url = apiUrl(`/api/v1/stream?channels=${options.channels.join(',')}`);
    const response = await doFetch(url, { headers, signal: stop.signal });
    if (response.status === 401) return 'unauthorized';
    if (!response.ok || !response.body) throw new Error(`stream ${response.status}`);

    options.onState?.('live');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return 'ended';
      buffer += decoder.decode(value, { stream: true });
      const parsed = parseFrames(buffer);
      buffer = parsed.rest;
      for (const frame of parsed.frames) {
        if (frame.id) lastEventId = frame.id;
        if (frame.event === RESYNC_EVENT) {
          options.onResync();
          continue;
        }
        let data: unknown;
        try {
          data = JSON.parse(frame.data);
        } catch {
          continue;
        }
        const message = StreamMessageSchema.safeParse({ type: frame.event, data });
        if (message.success) options.onMessage(message.data);
      }
    }
  }

  void (async () => {
    let attempt = 0;
    options.onState?.('connecting');
    while (!stop.signal.aborted) {
      try {
        const outcome = await connectOnce();
        // A clean end after being live is not a failure: retry promptly.
        if (outcome === 'ended') attempt = 0;
        else attempt += 1;
      } catch {
        if (stop.signal.aborted) return;
        attempt += 1;
      }
      if (stop.signal.aborted) return;
      options.onState?.('reconnecting');
      await sleep(backoffDelay(attempt, options.random), stop.signal);
    }
  })();

  return () => stop.abort();
}
